import React from 'react';
import SelecteurProduction from './SelecteurProduction';
import { RCP_OPTIONS } from '../constants';
import { api } from '../api/api';
import { useApiData } from '../hooks/useFetch';
import {
  CULTURES_EAU3MOIS,
  ANIMAUX_EAU3MOIS,
  useParametresEau3Mois,
} from '../hooks/useParametresEau3Mois';
import { GRANDEURS_CULTURE, formatJJMMAAAA } from '../utils/eau3mois';

// Seuls les RCP 4.5 et 8.5 existent dans les données du Shiny : hybride et
// TRACC sont affichés mais désactivés.
const RCP_ACTIFS = ['4.5', '8.5'];

/**
 * Sidebar commune aux pages "Eau à 3 mois".
 *   - production (culture / animal + semis / alimentation / abreuvement) :
 *     seulement sur les pages qui raisonnent par système (`avecProduction`)
 *   - scénario climatique, grandeur affichée, date de coupure : partout
 * `avecGrandeur` : false sur Total territoire (composition fixe, pas de
 *   grandeur à choisir).
 * `grandeurDefaut` : grandeur utilisée tant que l'utilisateur n'a rien choisi.
 * `total` : la page raisonne sur la composition fixe du territoire (pas de
 *   système choisi) ; les coupures proposées sont alors celles du total.
 */
export default function ParametresEau3Mois({ avecProduction = false, avecGrandeur = true, grandeurDefaut = 'culture', total = false }) {
  const { production, rcp, grandeur, coupure, modifier } = useParametresEau3Mois();
  const elevage = production.type === 'elevage';
  const grandeurActive = grandeur ?? grandeurDefaut;

  const { data: options } = useApiData(api.getEau3MoisOptions, {
    type: production.type,
    idProduction: production.id,
    semis: production.semis,
    alimentation: production.alimentation,
    abreuvement: production.abreuvement,
    total,
  });
  const coupures = options?.coupures ?? [];

  // La coupure choisie doit exister pour la production courante : sinon on
  // retombe sur la première (même garde-fou que le Shiny).
  React.useEffect(() => {
    if (coupures.length && !coupures.includes(coupure)) modifier({ coupure: coupures[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupures.join('|'), coupure]);

  return (
    <>
      {avecProduction && (
        <SelecteurProduction
          valeur={production}
          onChange={(v) => modifier({ production: v })}
          cultures={CULTURES_EAU3MOIS}
          animaux={ANIMAUX_EAU3MOIS}
        />
      )}

      <fieldset className="sidebar-champ sidebar-groupe">
        <legend className="sidebar-label">Scénarios climatiques</legend>
        {RCP_OPTIONS.map((option) => (
          <label key={option.value} className="sidebar-radio">
            <input
              type="radio"
              name="scenario-clim-eau3mois"
              value={option.value}
              checked={rcp === option.value}
              disabled={!RCP_ACTIFS.includes(option.value)}
              onChange={(e) => modifier({ rcp: e.target.value })}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      {/* Culture : liste déroulante. Élevage : un seul choix possible, donc du
          texte brut (pas de menu à un seul élément). */}
      {!avecGrandeur ? null : avecProduction && elevage ? (
        <div className="sidebar-champ">
          <span className="sidebar-label">Grandeur affichée</span>
          <span>Abreuvement</span>
        </div>
      ) : (
        <label className="sidebar-champ">
          <span className="sidebar-label">Grandeur affichée</span>
          <select value={grandeurActive} onChange={(e) => modifier({ grandeur: e.target.value })}>
            {GRANDEURS_CULTURE.map((g) => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
        </label>
      )}

      <label className="sidebar-champ">
        <span className="sidebar-label">Date de coupure</span>
        <select value={coupure ?? ''} onChange={(e) => modifier({ coupure: e.target.value })}>
          {coupures.map((c) => (
            <option key={c} value={c}>{formatJJMMAAAA(c)}</option>
          ))}
        </select>
      </label>
    </>
  );
}

/** Paramètres prêts pour l'API, ou null tant que la coupure n'est pas connue. */
export function useParametresPourApi({ grandeurDefaut = 'culture' } = {}) {
  const { production, rcp, grandeur, coupure } = useParametresEau3Mois();
  const type = production.type;
  return {
    pret: Boolean(coupure),
    production,
    parametres: {
      type,
      idProduction: production.id,
      semis: production.semis,
      alimentation: production.alimentation,
      abreuvement: production.abreuvement,
      rcp,
      grandeur: type === 'elevage' ? 'abreuvement' : grandeur ?? grandeurDefaut,
      coupure,
    },
  };
}
